// ASA Connect — Phase 2 data & analytics engine (frontend, derived/mock)
// Copyright & IP: Asal Shirazi.
import { symptomLabel } from "./data";

export const WEARABLE_DEVICES = [
  { id: "apple", name: "Apple Health" },
  { id: "fitbit", name: "Fitbit" },
  { id: "garmin", name: "Garmin" },
  { id: "oura", name: "Oura Ring" },
];

export const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const WEARABLE_DATA = {
  restingHR: { key: "restingHR", label: "Resting heart rate", unit: "bpm", today: 72, series: [64, 66, 65, 70, 69, 73, 72], normal: "55–70", higherIsWorse: true, threshold: 70 },
  hrv: { key: "hrv", label: "Heart rate variability", unit: "ms", today: 41, series: [55, 52, 50, 46, 44, 40, 41], normal: "≥45", higherIsWorse: false, threshold: 45 },
  sleep: { key: "sleep", label: "Sleep", unit: "h", today: 6.1, series: [7.5, 7.1, 6.8, 6.0, 5.8, 6.4, 6.1], normal: "7–9", higherIsWorse: false, threshold: 6.5 },
  steps: { key: "steps", label: "Steps", unit: "", today: 4200, series: [8000, 7400, 6000, 5200, 4800, 4500, 4200], normal: "≥7,000", higherIsWorse: false, threshold: 7000 },
  skinTemp: { key: "skinTemp", label: "Skin temp deviation", unit: "°C", today: 0.5, series: [0.0, 0.1, 0.2, 0.3, 0.5, 0.4, 0.5], normal: "±0.3", higherIsWorse: true, threshold: 0.3 },
};

export function loadDiary() {
  try { return JSON.parse(localStorage.getItem("asa_diary") || "[]"); } catch { return []; }
}
export function loadWearableConn() {
  try { return JSON.parse(localStorage.getItem("asa_wearables") || "{}"); } catch { return {}; }
}
export function saveWearableConn(obj) { localStorage.setItem("asa_wearables", JSON.stringify(obj)); }
export function anyWearableConnected() {
  const c = loadWearableConn();
  return Object.values(c).some(Boolean);
}

export function diaryStats(entries) {
  const freq = {};
  entries.forEach((e) => (e.symptoms || []).forEach((s) => (freq[s] = (freq[s] || 0) + 1)));
  const top = Object.entries(freq).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([id, count]) => ({ id, label: symptomLabel(id), count }));
  const flares = entries.filter((e) => e.flare).length;
  const recent = entries.slice(0, 7);
  const recentFlares = recent.filter((e) => e.flare).length;
  const avgSeverity = entries.length ? entries.reduce((s, e) => s + (e.severity || 0), 0) / entries.length : 0;
  const severitySeries = entries.slice(0, 7).reverse().map((e) => e.severity || 0);
  return { count: entries.length, top, flares, recentFlares, avgSeverity: Math.round(avgSeverity * 10) / 10, severitySeries };
}

export function generateInsights(stats) {
  const out = [];
  if (stats.count === 0) {
    out.push({ tone: "info", title: "Start logging to unlock insights", text: "Add a few symptom diary entries and connect a wearable so ASAL can spot patterns for you." });
    return out;
  }
  if (stats.top[0]) out.push({ tone: "info", title: "Your most frequent symptom", text: `“${stats.top[0].label}” appears in ${stats.top[0].count} of your ${stats.count} entries. Consider noting what precedes it.` });
  if (stats.avgSeverity >= 3.5) out.push({ tone: "warn", title: "Elevated average severity", text: `Your average severity is ${stats.avgSeverity}/5. Pacing and rest may help — see the Education Library.` });
  else out.push({ tone: "good", title: "Severity is manageable", text: `Your average severity is ${stats.avgSeverity}/5. Keep tracking to maintain the trend.` });
  if (stats.recentFlares >= 2) out.push({ tone: "warn", title: "Cluster of recent symptom episodes", text: `You logged ${stats.recentFlares} symptom episodes in your last 7 entries. Review possible triggers with your clinician.` });
  out.push({ tone: "info", title: "Tip", text: "Entries logged consistently at the same time of day produce the most reliable insights." });
  return out;
}

export function predictFlare(stats, w = WEARABLE_DATA, connected = true) {
  let risk = 18;
  const factors = [];
  risk += Math.min(30, stats.avgSeverity * 6);
  if (stats.avgSeverity >= 3) factors.push({ label: "Recent symptom severity", impact: "high" });
  risk += Math.min(20, stats.recentFlares * 8);
  if (stats.recentFlares >= 1) factors.push({ label: `${stats.recentFlares} recent symptom episode(s) logged`, impact: stats.recentFlares >= 2 ? "high" : "med" });
  if (connected) {
    if (w.sleep.today < w.sleep.threshold) { risk += 10; factors.push({ label: `Low sleep (${w.sleep.today}h)`, impact: "med" }); }
    if (w.hrv.today < w.hrv.threshold) { risk += 12; factors.push({ label: `Reduced HRV (${w.hrv.today}ms)`, impact: "high" }); }
    if (w.restingHR.today > w.restingHR.threshold) { risk += 8; factors.push({ label: `Raised resting HR (${w.restingHR.today}bpm)`, impact: "med" }); }
    if (w.skinTemp.today > w.skinTemp.threshold) { risk += 8; factors.push({ label: `Skin temp above baseline (+${w.skinTemp.today}°C)`, impact: "med" }); }
  }
  risk = Math.max(5, Math.min(95, Math.round(risk)));
  const level = risk < 35 ? "Low" : risk < 65 ? "Moderate" : "High";
  const base = risk;
  const forecast = DAYS.map((d, i) => ({ day: d, risk: Math.max(5, Math.min(95, Math.round(base + Math.sin(i) * 8 - i * 1.5))) }));
  return { risk, level, factors, forecast };
}

export const riskColor = (level) => (level === "High" ? "#e0567a" : level === "Moderate" ? "#e08a2b" : "#22a35a");
